import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, X, Save, Loader2, Wine } from 'lucide-react';
import { getCollaborators, adminCreateCollab, adminUpdateCollab, adminDeleteCollab, resolveImg } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import ImageUploader from '../../components/admin/ImageUploader';
import I18nField from '../../components/admin/I18nField';

const i18nEs = (v) => (typeof v === 'string' ? v : (v && v.es) || '');

const CATS = [{ v: 'bodega', l: 'Bodega' }, { v: 'partner', l: 'Asociado' }, { v: 'sponsor', l: 'Patrocinador' }];
const empty = () => ({ name: '', category: 'bodega', subtitle: '', logo: '', url: '', order: 0 });

export default function AdminColaboradores() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const { toast } = useToast();
  const load = () => { setLoading(true); getCollaborators().then(setItems).finally(() => setLoading(false)); };
  useEffect(load, []);

  const save = async () => {
    try {
      if (editing.id) await adminUpdateCollab(editing.id, editing);
      else await adminCreateCollab(editing);
      toast({ title: 'Guardado' }); setEditing(null); load();
    } catch (e) { toast({ title: 'Error', variant: 'destructive' }); }
  };
  const remove = async (id) => { if (confirm('¿Borrar?')) { await adminDeleteCollab(id); load(); } };
  const shown = filter === 'all' ? items : items.filter((i) => i.category === filter);

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="font-serif-display text-3xl text-white mb-1">Colaboradores</h1><p className="text-white/55">Bodegas, asociados y patrocinadores</p></div>
        <button onClick={() => setEditing(empty())} className="gd-btn-gold px-5 py-2.5 text-[11px] uppercase tracking-[0.3em] font-medium inline-flex items-center gap-2"><Plus size={14} /> Nuevo</button>
      </div>
      <div className="flex gap-2 mb-6">
        {[{ v: 'all', l: 'Todos' }, ...CATS].map((c) => (
          <button key={c.v} onClick={() => setFilter(c.v)} className={`px-4 py-1.5 text-[11px] uppercase tracking-widest border ${filter === c.v ? 'bg-[#c9a063] text-black border-[#c9a063]' : 'border-white/15 text-white/60'}`}>{c.l}</button>
        ))}
      </div>
      {loading ? <Loader2 className="animate-spin text-[#c9a063]" /> : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {shown.map((c) => (
            <div key={c.id} className="gd-card p-4 text-center">
              <div className="aspect-[3/2] bg-zinc-900 mb-3 flex items-center justify-center overflow-hidden">
                {c.logo ? <img src={resolveImg(c.logo)} alt={c.name} className="max-h-full max-w-full object-contain" /> : <Wine className="text-[#c9a063]/30" size={32} />}
              </div>
              <div className="text-[9px] tracking-widest text-[#c9a063] uppercase mb-1">{CATS.find((x) => x.v === c.category)?.l}</div>
              <div className="font-serif-display text-white text-sm leading-tight">{c.name}</div>
              {c.subtitle && <div className="text-white/50 text-xs mt-1">{c.subtitle}</div>}
              <div className="flex gap-1 justify-center mt-3">
                <button onClick={() => setEditing({ ...c })} className="text-xs px-2 py-1 border border-white/15 text-white/70 hover:text-[#c9a063] hover:border-[#c9a063]"><Edit2 size={11} /></button>
                <button onClick={() => remove(c.id)} className="text-xs px-2 py-1 border border-white/15 text-white/70 hover:text-red-400 hover:border-red-400"><Trash2 size={11} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/80 z-50 overflow-y-auto">
          <div className="min-h-screen flex items-start justify-center p-4">
            <div className="bg-[#0d0d0e] border border-white/10 w-full max-w-xl my-8">
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
                <h2 className="font-serif-display text-2xl text-white">{editing.id ? 'Editar' : 'Nuevo'} colaborador</h2>
                <button onClick={() => setEditing(null)}><X size={20} className="text-white/60" /></button>
              </div>
              <div className="p-6 space-y-4">
                <Field label="Nombre *"><input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={inputCls} /></Field>
                <Field label="Categoría *"><select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className={inputCls}>{CATS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}</select></Field>
                <Field label="Subtítulo (región / categoría)"><I18nField value={editing.subtitle} onChange={(v) => setEditing({ ...editing, subtitle: v })} testId="collab-subtitle" /></Field>
                <Field label="URL del sitio"><input value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value })} className={inputCls} placeholder="https://..." /></Field>
                <Field label="Logo"><ImageUploader value={editing.logo} onChange={(v) => setEditing({ ...editing, logo: v })} folder="bodegas" specKey="bodega_logo" /></Field>
                <Field label="Orden"><input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value) || 0 })} className={inputCls + ' w-24'} /></Field>
              </div>
              <div className="border-t border-white/10 px-6 py-4 flex justify-end gap-3">
                <button onClick={() => setEditing(null)} className="px-5 py-2.5 text-xs uppercase tracking-widest text-white/70">Cancelar</button>
                <button onClick={save} className="gd-btn-gold px-6 py-2.5 text-[11px] uppercase tracking-[0.3em] font-medium inline-flex items-center gap-2"><Save size={13} /> Guardar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
const inputCls = 'bg-zinc-950 border border-white/10 text-white/90 px-3 py-2 text-sm focus:border-[#c9a063] focus:outline-none w-full';
const Field = ({ label, children }) => <div><label className="text-[10px] tracking-widest text-white/50 uppercase block mb-1.5">{label}</label>{children}</div>;
